"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PaymentsInvoices = PaymentsInvoices;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const elements_1 = require("@src/components/elements");
const admin_context_1 = require("@src/components/pages/admin/admin-context");
const FormattedDate_1 = require("@src/components/modules/FormattedDate");
const react_intl_1 = require("react-intl");
const antd_1 = require("antd");
const react_styled_1 = require("styled-breakpoints/react-styled");
const styled_breakpoints_1 = require("styled-breakpoints");
const types_1 = require("@src/types");
const styled_components_1 = __importDefault(require("styled-components"));
const InvoiceTypeTag = styled_components_1.default.div `
	margin-top: 0.125rem;
	margin-bottom: 0.125rem;
	display: inline-block;

	.ant-tag {
		margin-top: 0.125rem;
		margin-bottom: 0.125rem;
	}
`;
function getInvoiceProductId(invoice) {
    if (invoice.companyPaymentCreditId) {
        return types_1.ProductId.offerCredits;
    }
    if (invoice.companyPaymentOfferPromotionId) {
        return types_1.ProductId.offerPromotion;
    }
    if (invoice.companyPaymentSubscriptionId) {
        return types_1.ProductId.companyPremium;
    }
    return types_1.ProductId.offerCV;
}
function PaymentsInvoices() {
    const { companyInfo } = (0, admin_context_1.useAdminContextValue)();
    const intl = (0, react_intl_1.useIntl)();
    const isLgOrLowerLayout = (0, react_styled_1.useBreakpoint)((0, styled_breakpoints_1.down)('lg'));
    const paymentsTableScroll = isLgOrLowerLayout ? { x: 1000 } : undefined;
    const invoices = companyInfo.invoices || [];
    const getIsInvoicePaid = (targetInvoice) => {
        if (!targetInvoice.isProforma) {
            return true;
        }
        const hasMatchingNonProformaInvoice = invoices.some(inv => {
            return (inv.variableSymbol === targetInvoice.variableSymbol &&
                inv.amountPaid === targetInvoice.amountPaid &&
                inv.id !== targetInvoice.id);
        });
        return hasMatchingNonProformaInvoice;
    };
    // Group invoices by companyPaymentCreditId (or other relevant IDs)
    const mergeInvoices = (invoiceList) => {
        const mergedMap = {};
        invoiceList.forEach(invoice => {
            let key = '';
            // Determine the grouping key based on payment type and variable symbol
            // to ensure multiple pairs with the same subscription/credit/promotion ID don't get merged
            if (invoice.companyPaymentCreditId) {
                key = `credit-${invoice.companyPaymentCreditId}-${invoice.variableSymbol}`;
            }
            else if (invoice.companyPaymentOfferPromotionId) {
                key = `promotion-${invoice.companyPaymentOfferPromotionId}-${invoice.variableSymbol}`;
            }
            else if (invoice.companyPaymentSubscriptionId) {
                key = `subscription-${invoice.companyPaymentSubscriptionId}-${invoice.variableSymbol}`;
            }
            else {
                // If no payment ID exists, use the invoice ID as a unique key
                key = `invoice-${invoice.id}`;
            }
            if (!mergedMap[key]) {
                mergedMap[key] = {
                    id: invoice.id,
                    createdAt: invoice.createdAt,
                    amountPaid: invoice.amountPaid,
                    amountPaidWithVat: invoice.amountPaidWithVat,
                    companyPaymentCreditId: invoice.companyPaymentCreditId,
                    companyPaymentOfferPromotionId: invoice.companyPaymentOfferPromotionId,
                    companyPaymentSubscriptionId: invoice.companyPaymentSubscriptionId,
                    variableSymbol: invoice.variableSymbol,
                    proformaInvoice: invoice.isProforma ? invoice : null,
                    regularInvoice: invoice.isProforma ? null : invoice,
                };
            }
            else {
                // Update the merged entry with this invoice
                if (invoice.isProforma) {
                    mergedMap[key].proformaInvoice = invoice;
                }
                else {
                    mergedMap[key].regularInvoice = invoice;
                }
                // Use the most recent date for the merged entry
                if (invoice.createdAt && mergedMap[key].createdAt) {
                    if (new Date(invoice.createdAt) > new Date(mergedMap[key].createdAt)) {
                        mergedMap[key].createdAt = invoice.createdAt;
                    }
                }
            }
        });
        return Object.values(mergedMap);
    };
    const mergedInvoices = mergeInvoices(invoices);
    const mergedInvoicesSortedByCreatedAt = mergedInvoices.length > 0
        ? [...mergedInvoices].sort((fst, snd) => new Date(fst.createdAt) > new Date(snd.createdAt) ? -1 : 1)
        : [];
    // Separate invoices into paid and unpaid
    const unpaidInvoices = mergedInvoicesSortedByCreatedAt.filter(invoice => invoice.proformaInvoice && !invoice.regularInvoice);
    const paidInvoices = mergedInvoicesSortedByCreatedAt.filter(invoice => invoice.regularInvoice);
    // Create sections for table
    const invoicesWithSections = (0, elements_1.createTableSections)({
        'admin.payments.invoices.waiting_for_payment': unpaidInvoices,
        'admin.payments.invoices.paid': paidInvoices
    }, 'invoice-section');
    const columns = [
        {
            title: intl.formatMessage({ id: 'admin.payments.invoices.product.title' }),
            key: 'product',
            align: 'center',
            render: (invoice) => {
                const invoiceProductId = getInvoiceProductId(invoice);
                if (invoiceProductId === types_1.ProductId.offerCredits) {
                    return ((0, jsx_runtime_1.jsx)(CustomTag, { children: (0, jsx_runtime_1.jsx)(antd_1.Tag, { color: "magenta", children: (0, jsx_runtime_1.jsx)(react_intl_1.FormattedMessage, { id: "admin.payments.invoices.product.credit" }) }) }));
                }
                if (invoiceProductId === types_1.ProductId.offerPromotion) {
                    return ((0, jsx_runtime_1.jsx)(CustomTag, { children: (0, jsx_runtime_1.jsx)(antd_1.Tag, { color: "purple", children: (0, jsx_runtime_1.jsx)(react_intl_1.FormattedMessage, { id: "admin.payments.invoices.product.promotion" }) }) }));
                }
                if (invoiceProductId === types_1.ProductId.companyPremium) {
                    return ((0, jsx_runtime_1.jsx)(CustomTag, { children: (0, jsx_runtime_1.jsx)(antd_1.Tag, { color: "purple", children: (0, jsx_runtime_1.jsx)(react_intl_1.FormattedMessage, { id: "admin.payments.invoices.product.company_premium_profile" }) }) }));
                }
                if (invoiceProductId === types_1.ProductId.offerCV) {
                    return ((0, jsx_runtime_1.jsx)(CustomTag, { children: (0, jsx_runtime_1.jsx)(antd_1.Tag, { color: "magenta", children: (0, jsx_runtime_1.jsx)(react_intl_1.FormattedMessage, { id: "admin.payments.invoices.product.offer_cv" }) }) }));
                }
                return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, {});
            },
        },
        {
            title: intl.formatMessage({
                id: 'admin.payments.invoices.created_at',
            }),
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'center',
            render: (createdAt) => {
                return (0, jsx_runtime_1.jsx)(FormattedDate_1.FormattedDate, { value: createdAt, day: "numeric", month: "numeric", year: "numeric" });
            },
        },
        {
            title: intl.formatMessage({
                id: 'admin.payments.invoices.total_without_vat',
            }),
            key: 'amount',
            align: 'center',
            render: (invoice) => {
                const amountWithVat = invoice.amountPaidWithVat;
                const amountWithoutVat = invoice.amountPaid;
                const isWithoutVAT = amountWithVat === amountWithoutVat;
                if (isWithoutVAT) {
                    return ((0, jsx_runtime_1.jsxs)("span", { children: [(0, jsx_runtime_1.jsx)(react_intl_1.FormattedNumber, { value: parseFloat(amountWithoutVat), style: "currency", currency: "EUR" }), ' '] }));
                }
                return ((0, jsx_runtime_1.jsx)(antd_1.Tooltip, { placement: "top", title: intl.formatMessage({
                        id: 'admin.payments.price_with_vat',
                    }, {
                        price: parseFloat(amountWithVat).toFixed(2),
                    }), children: (0, jsx_runtime_1.jsxs)("span", { children: [(0, jsx_runtime_1.jsx)(react_intl_1.FormattedNumber, { value: parseFloat(amountWithoutVat), style: "currency", currency: "EUR" }), ' '] }) }));
            },
        },
        {
            key: 'download',
            fixed: isLgOrLowerLayout ? 'right' : undefined,
            align: 'right',
            width: isLgOrLowerLayout ? 220 : undefined,
            render: (invoice) => {
                const hasProforma = !!invoice.proformaInvoice;
                const hasRegular = !!invoice.regularInvoice;
                if (hasProforma && hasRegular) {
                    return ((0, jsx_runtime_1.jsxs)(antd_1.Space, { children: [(0, jsx_runtime_1.jsx)(elements_1.Button, { onClick: () => window.open(invoice.proformaInvoice.url, 'blank'), size: "middle", type: "default", children: (0, jsx_runtime_1.jsx)(react_intl_1.FormattedMessage, { id: "admin.payments.credits.download_proforma_invoice" }) }), (0, jsx_runtime_1.jsx)(elements_1.Button, { onClick: () => window.open(invoice.regularInvoice.url, 'blank'), size: "middle", type: "primary", children: (0, jsx_runtime_1.jsx)(react_intl_1.FormattedMessage, { id: "admin.payments.credits.download_invoice" }) })] }));
                }
                else if (hasProforma) {
                    return ((0, jsx_runtime_1.jsx)(elements_1.Button, { onClick: () => window.open(invoice.proformaInvoice.url, 'blank'), size: "middle", warning: true, children: (0, jsx_runtime_1.jsx)(react_intl_1.FormattedMessage, { id: "admin.payments.credits.download_proforma_invoice" }) }));
                }
                else if (hasRegular) {
                    return ((0, jsx_runtime_1.jsx)(elements_1.Button, { onClick: () => window.open(invoice.regularInvoice.url, 'blank'), size: "middle", type: "primary", children: (0, jsx_runtime_1.jsx)(react_intl_1.FormattedMessage, { id: "admin.payments.credits.download_invoice" }) }));
                }
                return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, {});
            },
        },
    ];
    return ((0, jsx_runtime_1.jsx)(elements_1.Table, { enableSections: true, pagination: false, columns: columns, dataSource: invoicesWithSections, scroll: paymentsTableScroll, rowKey: "id" }));
}
const CustomTag = styled_components_1.default.div `
	width: fit-content;
	margin: auto;
`;
